import { z } from "zod";

// TES-978 "JOURNALENS KÄLLOR OCH VERSIONER":
//   "För varje förifylld uppgift ska Kirill kunna se källa, datum, bakomliggande fråga och patientens originalsvar."
// Each prefilled line carries its source. The box text is what the doctor
// edits; the source is what the line was built from, and never changes.
export const JOURNAL_PREFILL_SOURCE_KINDS = [
  "form",
  "measurement",
  "prescription_cycle",
  // TES-1057 — the doctor's review of a prescription document.
  "prescription_evidence_review",
  // TES-1058 — an analysed value from a lab order (LabResult).
  "lab_result",
] as const;

export const journalPrefillSourceSchema = z.object({
  kind: z.enum(JOURNAL_PREFILL_SOURCE_KINDS),
  // The form's readable name, or where a reading was entered, or the order.
  label: z.string(),
  // The stored form key ("weekly_checkin"); null for a reading or an order.
  templateKey: z.string().nullable(),
  // The submission, measurement session or prescription cycle it came from.
  sourceId: z.string().nullable(),
  // The question as she was asked it, and her answer as she gave it.
  question: z.string().nullable(),
  answer: z.string().nullable(),
  // When she answered, the reading was taken, or the order was written.
  date: z.string().nullable(),
  // TES-1019 (Emelie, point 1) — the form version she answered, shown under
  // the source beside the form's name and the submission time. Absent on a
  // source recorded before TES-1019, and on a reading or an order.
  templateVersion: z.number().int().nullable().optional(),
});
export type JournalPrefillSourceRef = z.infer<
  typeof journalPrefillSourceSchema
>;

export const journalPrefillLineSchema = z.object({
  text: z.string(),
  // Null for a line that only structures the box (a blank between groups).
  source: journalPrefillSourceSchema.nullable(),
  // TES-984 — a compact line that sums up several answers ("Nej" in three
  // weeks, "Ja" in one) carries every one of them here, oldest first, so each
  // original answer, date and question stays reachable; `source` is then the
  // newest of them. Absent on a line built from one answer.
  sources: z.array(journalPrefillSourceSchema).optional(),
  // TES-1050 (Ö-278 b) — an answer that is shown only behind "Källa": no
  // phrasing is mapped for its field, or it is a plain "no" the mapping does
  // not keep. It is never part of the box's text (`text` is empty).
  sourceOnly: z.literal(true).optional(),
});
export type JournalPrefillLine = z.infer<typeof journalPrefillLineSchema>;

/** A box's prefilled text: its lines, one per row ("Källa"-only left out). */
export function journalPrefillText(
  lines: readonly JournalPrefillLine[],
): string {
  return lines
    .filter((l) => !l.sourceOnly)
    .map((l) => l.text)
    .join("\n");
}

// ---- the versioned representations (TES-904 step 5) ------------------------

// Emelie:
// "AI-utkast, Kirills stödord, redigerade journalsektioner och slutlig journaltext ska vara separata, versionshanterade representationer."
// One append-only history per note (journal_revisions). The section rows stay
// the working copy; these are what was proposed, typed and copied, and when.
export const JOURNAL_REVISION_KINDS = [
  "prefill",
  "ai_draft",
  "stodord",
  "edit",
  "final",
] as const;
export const journalRevisionKindSchema = z.enum(JOURNAL_REVISION_KINDS);
export type JournalRevisionKind = z.infer<typeof journalRevisionKindSchema>;

// The prefill a box was built from, with a source for every line
// (the admin's "Källa" under the box).
export const journalSectionSourceSchema = z.object({
  revisionId: z.string().uuid(),
  createdAt: z.string(),
  lines: z.array(journalPrefillLineSchema),
});
export type JournalSectionSource = z.infer<typeof journalSectionSourceSchema>;

// A newer prefill than the doctor's text, never written into the box by
// itself.
// "Nytt formulärunderlag får inte skriva över Kirills tidigare redigering utan ska markeras som nytt källunderlag."
// TES-1244 (TES-1240) — offered as "Begärd komplettering har kommit in" when
// one of its lines answers an item of a completion round (`requested`), else
// as "Ny information har kommit in". Optional: absent from an API before
// TES-1244, which reads as new information.
export const journalNewSourceSchema = journalSectionSourceSchema.extend({
  content: z.string(),
  requested: z.boolean().optional(),
});
export type JournalNewSource = z.infer<typeof journalNewSourceSchema>;

// The doctor's shorthand for the whole note, as last saved.
export const JOURNAL_STODORD_MAX = 4000;
export const journalStodordSchema = z.object({
  revisionId: z.string().uuid(),
  content: z.string(),
  createdAt: z.string(),
});
export type JournalStodord = z.infer<typeof journalStodordSchema>;

// PUT …/journal/stodord — autosaved; a save equal to the last one adds no
// version.
export const saveJournalStodordSchema = z.object({
  content: z.string().max(JOURNAL_STODORD_MAX),
});
export type SaveJournalStodord = z.infer<typeof saveJournalStodordSchema>;

// POST …/journal/final — "Sammanställ journal": the exact text copied to
// Webdoc. Ten boxes of at most 10 000 characters, plus headings.
export const JOURNAL_FINAL_MAX = 110_000;
export const finalizeJournalSchema = z.object({
  text: z.string().trim().min(1).max(JOURNAL_FINAL_MAX),
});
export type FinalizeJournal = z.infer<typeof finalizeJournalSchema>;

// The stored final version: what the "Införd i Webdoc" receipt cites.
export const journalFinalVersionSchema = z.object({
  revisionId: z.string().uuid(),
  createdAt: z.string(),
});
export type JournalFinalVersion = z.infer<typeof journalFinalVersionSchema>;

// ---- "Införd i Webdoc", per version (TES-978) ------------------------------

// Emelie:
// "Efter kopiering ska Kirill kunna markera journalversionen som 'Införd i Webdoc'. Det är en manuell kvittens med läkare, journalversion och tidpunkt – inte ett påstående om att Ella tekniskt har verifierat innehållet i Webdoc."
//
// A final version as the doctor reads it: its number is its place among the
// note's final versions, 1 for the first "Sammanställ journal".
export const journalVersionRefSchema = z.object({
  revisionId: z.string().uuid(),
  version: z.number().int().positive(),
  createdAt: z.string(),
});
export type JournalVersionRef = z.infer<typeof journalVersionRefSchema>;

export const journalWebdocReceiptSchema = z.object({
  revisionId: z.string().uuid(),
  version: z.number().int().positive(),
  confirmedAt: z.string(),
  confirmedByEmployeeId: z.string().uuid(),
  // Null when the employee row cannot be read (it never blocks the receipt).
  confirmedByDisplayName: z.string().nullable(),
});
export type JournalWebdocReceipt = z.infer<typeof journalWebdocReceiptSchema>;

export const journalWebdocStatusSchema = z.object({
  // The newest final version; null before the first "Sammanställ journal".
  latestFinal: journalVersionRefSchema.nullable(),
  // The newest receipt; null when no version has been marked.
  receipt: journalWebdocReceiptSchema.nullable(),
  // The note changed after the marked version: a box was saved, or the text
  // was assembled again. What is in Webdoc may then not be the note as it
  // stands, and the admin says so.
  changedSinceReceipt: z.boolean(),
});
export type JournalWebdocStatus = z.infer<typeof journalWebdocStatusSchema>;

// POST …/journal/webdoc — the version the doctor marks. Only the newest
// final version can be marked: the one on his screen after
// "Sammanställ journal".
export const markJournalWebdocSchema = z.object({
  revisionId: z.string().uuid(),
});
export type MarkJournalWebdoc = z.infer<typeof markJournalWebdocSchema>;

// "Sammanställ journal"'s answer: the version it stored, and the receipt
// status that follows from it.
export const journalFinalizedSchema = journalFinalVersionSchema.extend({
  version: z.number().int().positive(),
  webdoc: journalWebdocStatusSchema,
});
export type JournalFinalized = z.infer<typeof journalFinalizedSchema>;

function receiptTime(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
  });
}

/**
 * What the admin says about the receipt: who marked which version, and when
 * (the wording suggested in TES-978). It reports the doctor's own marking and
 * nothing about Webdoc's content, which Ella has not seen.
 * Null when there is nothing to say (no receipt yet).
 */
export function journalWebdocStatusLine(
  status: JournalWebdocStatus,
): string | null {
  const r = status.receipt;
  if (!r) return null;
  const at = receiptTime(r.confirmedAt);
  if (status.changedSinceReceipt) {
    return `Version ${r.version} införd i Webdoc ${at}; senare ändringar är inte markerade.`;
  }
  const by = r.confirmedByDisplayName ? ` av ${r.confirmedByDisplayName}` : "";
  return `Införd i Webdoc${by} ${at} (version ${r.version}).`;
}

/** The newest final version is not yet marked: offer the button. */
export function canMarkJournalWebdoc(status: JournalWebdocStatus): boolean {
  return (
    status.latestFinal !== null &&
    status.receipt?.revisionId !== status.latestFinal.revisionId
  );
}
